import { useEffect } from 'react';

// Number of open drawers holding the pin, and the page scroll to return to once the last closes
const COUNT_ATTRIBUTE = 'data-ios-pin-count';
const SCROLL_ATTRIBUTE = 'data-ios-pin-scroll';
const PINNED_STYLES = ['position', 'top', 'left', 'right'] as const;

// iPadOS reports itself as a Mac, touch support tells them apart
const isIOS = () =>
  /^(iPhone|iPad|iPod)/.test(navigator.platform) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// vaul pins the body itself only when the user agent says Safari, which in-app web views don't
const isSafariUserAgent = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const readNumber = (body: HTMLElement, attribute: string) =>
  Number(body.getAttribute(attribute)) || 0;

const pinBody = (body: HTMLElement) => {
  const { scrollY } = window;
  body.setAttribute(SCROLL_ATTRIBUTE, String(scrollY));
  // Important, as the modal scroll lock forces the body to relative with an important rule
  body.style.setProperty('position', 'fixed', 'important');
  body.style.setProperty('top', `${-scrollY}px`, 'important');
  body.style.setProperty('left', '0', 'important');
  body.style.setProperty('right', '0', 'important');
};

const unpinBody = (body: HTMLElement) => {
  const scrollY = readNumber(body, SCROLL_ATTRIBUTE);
  PINNED_STYLES.forEach((property) => body.style.removeProperty(property));
  body.removeAttribute(SCROLL_ATTRIBUTE);
  window.scrollTo(0, scrollY);
};

/**
 * Pins the body in place while a drawer is open in an iOS in-app web view. Otherwise iOS scrolls
 * the page under the drawer on every input focus, and the drawer and overlay jump.
 *
 * All open drawers share one pin, released when the last one closes. The open count lives on the
 * body, the same way Radix's modal scroll lock counts its locks in `data-scroll-locked`. Keeping
 * it next to the styles it guards means hot reloads and duplicate bundles can't lose track of it.
 */
export const useIosWebViewBodyPin = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen || !isIOS() || isSafariUserAgent()) return;
    const { body } = document;
    const count = readNumber(body, COUNT_ATTRIBUTE);
    if (count === 0) pinBody(body);
    body.setAttribute(COUNT_ATTRIBUTE, String(count + 1));

    return () => {
      const remaining = readNumber(body, COUNT_ATTRIBUTE) - 1;
      if (remaining > 0) {
        body.setAttribute(COUNT_ATTRIBUTE, String(remaining));
        return;
      }
      body.removeAttribute(COUNT_ATTRIBUTE);
      unpinBody(body);
    };
  }, [isOpen]);
};
